import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Save, Settings, Mail, Phone, Type, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSiteSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    brand_name: '',
    support_email: '',
    support_whatsapp: '',
    homepage_headline: '',
    homepage_subheadline: '',
    refund_policy_snippet: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setSaving(true);
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-playfair text-4xl font-bold mb-2">Site Settings</h1>
          <p className="font-manrope text-sm text-gray-600">Manage homepage content and support information</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="flex items-center gap-2 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-8">
        {/* Brand Settings */}
        <div className="border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-5 h-5" />
            <h2 className="font-playfair text-xl font-bold">Brand Settings</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Brand Name</label>
              <input
                type="text"
                value={settings.brand_name}
                onChange={(e) => setSettings({...settings, brand_name: e.target.value})}
                data-testid="brand-name-input"
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Support Contact */}
        <div className="border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Phone className="w-5 h-5" />
            <h2 className="font-playfair text-xl font-bold">Support Contact</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Support Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => setSettings({...settings, support_email: e.target.value})}
                  data-testid="support-email-input"
                  className="w-full border border-gray-200 pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Support WhatsApp</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={settings.support_whatsapp}
                  onChange={(e) => setSettings({...settings, support_whatsapp: e.target.value})}
                  placeholder="+91 9876543210"
                  data-testid="support-whatsapp-input"
                  className="w-full border border-gray-200 pl-10 pr-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Homepage Content */}
        <div className="border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Type className="w-5 h-5" />
            <h2 className="font-playfair text-xl font-bold">Homepage Content</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Hero Headline</label>
              <input
                type="text"
                value={settings.homepage_headline}
                onChange={(e) => setSettings({...settings, homepage_headline: e.target.value})}
                data-testid="headline-input"
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <p className="font-manrope text-xs text-gray-500 mt-1">Main headline displayed in the hero section</p>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Hero Subheadline</label>
              <input
                type="text"
                value={settings.homepage_subheadline}
                onChange={(e) => setSettings({...settings, homepage_subheadline: e.target.value})}
                data-testid="subheadline-input"
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <p className="font-manrope text-xs text-gray-500 mt-1">Supporting text below the headline</p>
            </div>
            
            <div>
              <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Refund Policy Snippet</label>
              <input
                type="text"
                value={settings.refund_policy_snippet}
                onChange={(e) => setSettings({...settings, refund_policy_snippet: e.target.value})}
                data-testid="refund-snippet-input"
                className="w-full border border-gray-200 px-4 py-3 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <p className="font-manrope text-xs text-gray-500 mt-1">Short refund policy shown in trust strip</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSiteSettings;
